import React, { useMemo, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Chip,
  IconButton,
  Popover,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { DOCLINK_COLORS } from "@/config/colors";
import { hubMedAdminDataGridSx, docLinkPurpleDataGridSx } from "@/utils/adminTableStyles";
import { getChemistName } from "@/utils/textUtils";
import { formatHubMedDateOnly } from "@/utils/hubMedFormatting";
import { hubMedConsultLabel, hubMedConsultCount } from "./hubMedChartHelpers";

const BreakdownCell = ({ breakdown }) => {
  const [anchor, setAnchor] = useState(null);
  const list = Array.isArray(breakdown) ? breakdown : [];
  const chips = list
    .filter((c) => hubMedConsultCount(c) > 0)
    .slice(0, 3)
    .map((c) => ({
      label: hubMedConsultLabel(c),
      count: hubMedConsultCount(c),
    }));

  if (!list.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const showPopover = list.length > 3 || chips.length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        flexWrap: "wrap",
        py: 0.5,
      }}
    >
      {chips.map((c) => (
        <Chip
          key={c.label}
          size="small"
          label={`${c.label}: ${c.count}`}
          sx={{
            maxWidth: 160,
            "& .MuiChip-label": { px: 0.75, fontSize: 11 },
            bgcolor: DOCLINK_COLORS.LAVENDER_TINT,
            borderColor: DOCLINK_COLORS.LIGHT_PURPLE,
          }}
          variant="outlined"
        />
      ))}
      {showPopover && (
        <>
          <IconButton
            size="small"
            aria-label="View full consult type breakdown"
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
          <Popover
            open={Boolean(anchor)}
            anchorEl={anchor}
            onClose={() => setAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          >
            <Box sx={{ p: 2, maxWidth: 320 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Consult types
              </Typography>
              {list.map((c, i) => (
                <Typography key={i} variant="body2" sx={{ py: 0.25 }}>
                  <strong>{hubMedConsultLabel(c)}:</strong>{" "}
                  {hubMedConsultCount(c).toLocaleString()}
                </Typography>
              ))}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

const HubMedConnectedChemistsTable = ({
  chemists,
  loading,
  visible,
  /** Match Admin DocLink overview table styling */
  useDocLinkGridStyle = false,
  /** Extra consult-type column (e.g. detailed admin views) */
  showConsultBreakdown = true,
  cardTitle = "Connected chemists",
  /** Only the grid — parent supplies Card / toolbar (e.g. HubMed overview) */
  embedded = false,
  /** With `embedded`, grow to fill a flex parent instead of a fixed grid height */
  fillHeight = false,
}) => {
  const rows = useMemo(() => {
    const list = Array.isArray(chemists) ? chemists : [];
    return list.map((r, i) => ({
      id: r.chemistId ?? r.ChemistID ?? r.chemistUuid ?? r.ChemistUUID ?? `row-${i}`,
      chemistName: r.chemistName ?? r.ChemistName,
      latestAppointmentDate:
        r.latestAppointmentDate ?? r.LatestAppointmentDate ?? null,
      total: Number(r.totalAppointments ?? r.TotalAppointments ?? 0),
      breakdown: r.consultTypeBreakdown ?? r.ConsultTypeBreakdown,
    }));
  }, [chemists]);

  const baseColumns = [
    {
      field: "chemistName",
      headerName: "Chemist Name",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      align: "center",
      renderCell: (p) => getChemistName(p.value),
    },
    {
      field: "latestAppointmentDate",
      headerName: "Latest appointment",
      width: 150,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (p) =>
        p.value == null || p.value === ""
          ? "—"
          : formatHubMedDateOnly(p.value),
    },
    {
      field: "total",
      headerName: "Total appointments",
      width: 170,
      type: "number",
      headerAlign: "center",
      align: "center",
      renderCell: (p) => Number(p.value ?? 0).toLocaleString(),
    },
  ];

  const breakdownColumn = {
    field: "breakdown",
    headerName: "Consult types",
    flex: 1,
    minWidth: 220,
    sortable: false,
    headerAlign: "center",
    align: "center",
    renderCell: (p) => <BreakdownCell breakdown={p.row.breakdown} />,
  };

  const columns = showConsultBreakdown
    ? [...baseColumns, breakdownColumn]
    : baseColumns;

  const gridSx = useDocLinkGridStyle ? docLinkPurpleDataGridSx : hubMedAdminDataGridSx;
  const useFlexGrid = embedded && fillHeight;

  if (!visible) return null;

  const grid = useFlexGrid ? (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        width: "100%",
        px: 1,
        pb: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 20 } },
        }}
        disableRowSelectionOnClick
        loading={loading}
        sx={{ ...gridSx, height: "100%", minHeight: 0 }}
      />
    </Box>
  ) : (
    <div style={{ height: 520, width: "100%", padding: "0 8px 16px" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          pagination: { paginationModel: { pageSize: 20 } },
        }}
        disableRowSelectionOnClick
        loading={loading}
        sx={gridSx}
      />
    </div>
  );

  if (embedded) {
    return grid;
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: DOCLINK_COLORS.DARK_PURPLE,
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            fontWeight={700}
            sx={{ color: DOCLINK_COLORS.DARK_PURPLE }}
          >
            {cardTitle}
          </Typography>
        </Box>
        <Divider />
        {grid}
      </CardContent>
    </Card>
  );
};

export default HubMedConnectedChemistsTable;
